import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Button } from '../primitives/Button';
import { Eyebrow } from '../primitives/Eyebrow';
import type { TierRecord } from '../../content/schemas';

interface TierCardProps {
  tier: TierRecord;
}

const Card = styled.article<{ $featured?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.step[4]}px;
  padding: ${(p) => p.theme.step[6]}px;
  border-radius: 12px;
  background: ${(p) => p.theme.color.bg[1]};
  border: 1px solid ${(p) => p.theme.color.border.soft};
  ${(p) =>
    p.$featured &&
    css`
      border-color: ${p.theme.color.accent.amberLine};
      box-shadow: 0 0 0 4px ${p.theme.color.accent.amberGlow};
    `}
`;

const Name = styled.h3`
  margin: 0;
  font-family: ${(p) => p.theme.font.serif};
  font-size: ${(p) => p.theme.step[7]}px;
`;

const Tagline = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.step[3]}px;
  color: ${(p) => p.theme.color.text[2]};
`;

const Price = styled.div`
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[8]}px;
  letter-spacing: -0.02em;
  color: ${(p) => p.theme.color.text[0]};
`;

const Capital = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.step[1]}px;
  color: ${(p) => p.theme.color.text[3]};
  font-family: ${(p) => p.theme.font.mono};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Features = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Feature = styled.li<{ $excluded?: boolean }>`
  display: flex;
  gap: 10px;
  font-size: ${(p) => p.theme.step[3]}px;
  color: ${(p) => (p.$excluded ? p.theme.color.text[3] : p.theme.color.text[1])};
  ${(p) => p.$excluded && css`text-decoration: line-through;`}

  &::before {
    content: ${(p) => (p.$excluded ? '"✕"' : '"✓"')};
    color: ${(p) =>
      p.$excluded ? p.theme.color.text[4] : p.theme.color.semantic.up};
  }
`;

export function TierCard({ tier }: TierCardProps) {
  return (
    <Card $featured={tier.featured}>
      {tier.featured && <Eyebrow $variant="amber">MOST POPULAR</Eyebrow>}
      <Name>{tier.name}</Name>
      <Tagline>{tier.tagline}</Tagline>
      <Price>{typeof tier.price === 'number' ? `$${tier.price}/mo` : tier.price}</Price>
      <Capital>{tier.capital}</Capital>
      <Features>
        {tier.features.map((f) => (
          <Feature key={f}>{f}</Feature>
        ))}
        {tier.excluded.map((f) => (
          <Feature key={f} $excluded>
            {f}
          </Feature>
        ))}
      </Features>
      <Button as={Link} to={`/signup?tier=${tier.id}`} $variant={tier.featured ? 'primary' : 'ghost'}>
        {typeof tier.price === 'number' ? 'Choose plan' : 'Talk to sales'}
      </Button>
    </Card>
  );
}
